<template>
  <div class="qrcashier-page">
    <!-- 顶部标题和金额合并区 -->
    <header class="page-header">
      <div class="header-top">
        <div class="header-icon">💳</div>
        <div class="header-info">
          <h1 class="page-title">微信支付</h1>
          <div class="header-countdown">
            <span class="countdown-icon">⏰</span>
            <span class="countdown-text">{{ formattedCountdown }}</span>
          </div>
        </div>
      </div>
      <div class="amount-info" v-if="payOrderInfo.amount">
        <div class="amount-value">￥{{ formatAmount(payOrderInfo.amount) }}</div>
        <div class="amount-order">订单号：{{ payOrderInfo.mchOrderNo || '-' }}</div>
      </div>
    </header>

    <div class="content-wrapper">

      <!-- 二维码展示区域 -->
      <section class="qrcode-section">
        <div class="section-header">
          <span class="section-icon">📲</span>
          <h2 class="section-title">扫描二维码支付</h2>
        </div>
        <div class="qrcode-container">
          <div class="qrcode-wrapper" v-if="qrCodeUrl">
            <img :src="qrCodeUrl" alt="支付二维码" class="qrcode-image" @error="handleImageError">
            <div class="qrcode-mask" v-if="imageError">
              <span class="error-icon">⚠️</span>
              <span class="error-text">二维码加载失败</span>
            </div>
          </div>
          <div class="qrcode-placeholder" v-else>
            <span class="placeholder-icon">🔍</span>
            <span class="placeholder-text">加载中...</span>
          </div>
          <div class="qrcode-tip">
            <span class="tip-icon">💡</span>
            <span class="tip-text">请使用微信扫一扫功能扫描二维码</span>
          </div>
        </div>
      </section>

      <!-- 操作步骤引导 -->
      <section class="steps-section">
        <div class="section-header">
          <span class="section-icon">📋</span>
          <h2 class="section-title">支付步骤</h2>
        </div>
        <div class="steps-list">
          <div class="step-item">
            <div class="step-number">1</div>
            <div class="step-content">
              <div class="step-title">截屏保存二维码</div>
              <div class="step-desc">长按或截屏保存二维码到相册</div>
            </div>
          </div>
          <div class="step-item">
            <div class="step-number">2</div>
            <div class="step-content">
              <div class="step-title">打开微信扫一扫</div>
              <div class="step-desc">打开微信，点击右上角扫一扫图标</div>
            </div>
          </div>
          <div class="step-item">
            <div class="step-number">3</div>
            <div class="step-content">
              <div class="step-title">识别二维码</div>
              <div class="step-desc">点击相册，选择刚才保存的二维码</div>
            </div>
          </div>
          <div class="step-item">
            <div class="step-number">4</div>
            <div class="step-content">
              <div class="step-title">完成支付</div>
              <div class="step-desc">确认金额 <strong class="highlight-amount">￥{{ formatAmount(payOrderInfo.amount) }}</strong> 后完成支付</div>
            </div>
          </div>
        </div>
      </section>
    </div>

    <!-- 加载遮罩 -->
    <div class="processing-mask" v-if="processing">
      <div class="processing-card">
        <div class="spinner"></div>
        <p class="processing-text">{{ processingText }}</p>
      </div>
    </div>

    <!-- 提示Toast -->
    <transition name="fade">
      <div class="toast" v-if="showToast">
        <div class="toast-content">
          <span class="toast-icon">{{ toastIcon }}</span>
          <span>{{ toastMessage }}</span>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { getPayOrderInfo, getPayOrderInfoState } from '@/api/api'
import config from '@/config'

export default {
  name: 'CashierQrCashier',
  data () {
    return {
      payOrderInfo: {},
      qrCodeUrl: '',
      loading: false,
      processing: false,
      processingText: '等待支付确认，请稍候…',
      pollTimer: null,
      showToast: false,
      toastMessage: '',
      toastIcon: '✓',
      countdown: 1800, // 30分钟倒计时（秒）
      countdownTimer: null,
      orderToken: '',
      imageError: false
    }
  },
  mounted () {
    this.fetchPayOrderInfo()
    this.initCountdown()
    this.startOrderPolling()
  },
  beforeDestroy () {
    this.stopOrderPolling()
    this.stopCountdown()
  },
  computed: {
    formattedCountdown () {
      const minutes = Math.floor(this.countdown / 60)
      const seconds = this.countdown % 60
      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    }
  },
  methods: {
    fetchPayOrderInfo () {
      getPayOrderInfo().then(res => {
        console.log("获取到的订单信息", res)
        this.payOrderInfo = res || {}
        
        // 从响应中获取二维码URL
        // 优先使用 ext.codeImgUrl（后端为QR_CASHIER订单生成的二维码图片URL）
        if (res.ext && res.ext.codeImgUrl) {
          this.qrCodeUrl = res.ext.codeImgUrl
          console.log("使用 ext.codeImgUrl:", this.qrCodeUrl)
        } 
        // 其次使用 ext.qrCodeUrl（码商账户的二维码）
        else if (res.ext && res.ext.qrCodeUrl) {
          this.qrCodeUrl = res.ext.qrCodeUrl
          console.log("使用 ext.qrCodeUrl:", this.qrCodeUrl)
        }
        // 兼容统一下单API返回的 codeImgUrl
        else if (res.codeImgUrl) {
          this.qrCodeUrl = res.codeImgUrl
          console.log("使用 codeImgUrl:", this.qrCodeUrl)
        }
        else {
          console.warn("未能获取到二维码URL")
        }
        
        console.log("最终二维码URL:", this.qrCodeUrl)
        
        this.orderToken = res.payOrderId || res.mchOrderNo || ''
      }).catch(err => {
        console.error("获取订单信息失败", err)
        this.$router.push({ name: config.errorPageRouteName, params: { errInfo: err.msg || '获取订单信息失败' } })
      })
    },
    initCountdown () {
      const savedData = sessionStorage.getItem('payCountdown')
      if (savedData) {
        try {
          const { orderToken, expireTime } = JSON.parse(savedData)
          const now = Math.floor(Date.now() / 1000)
          const remaining = expireTime - now
          
          if (remaining > 0 && orderToken === this.orderToken) {
            this.countdown = remaining
          } else {
            sessionStorage.removeItem('payCountdown')
          }
        } catch (e) {
          console.error('恢复倒计时失败', e)
        }
      }
      this.startCountdown()
    },
    saveCountdownState () {
      if (this.orderToken) {
        const expireTime = Math.floor(Date.now() / 1000) + this.countdown
        sessionStorage.setItem('payCountdown', JSON.stringify({
          orderToken: this.orderToken,
          expireTime: expireTime
        }))
      }
    },
    startOrderPolling () {
      this.stopOrderPolling()
      this.pollTimer = setInterval(() => {
        getPayOrderInfoState().then(info => {
          const state = info && (info.state !== undefined ? info.state : info.orderState)
          if (state === 2) {
            this.stopOrderPolling()
            this.gotoSuccess(info.returnUrl)
          } else if (state === 3) {
            this.stopOrderPolling()
            this.gotoFail(info.errMsg || '支付失败，请重试', info.returnUrl)
          }
        }).catch(() => {})
      }, 2000)
    },
    stopOrderPolling () {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
      this.processing = false
    },
    startCountdown () {
      this.stopCountdown()
      this.saveCountdownState()
      
      this.countdownTimer = setInterval(() => {
        if (this.countdown > 0) {
          this.countdown--
          if (this.countdown % 10 === 0) {
            this.saveCountdownState()
          }
        } else {
          this.stopCountdown()
          sessionStorage.removeItem('payCountdown')
          this.gotoFail('订单已超时，请重新下单', this.payOrderInfo.returnUrl)
        }
      }, 1000)
    },
    stopCountdown () {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer)
        this.countdownTimer = null
      }
    },
    clearCountdownCache () {
      sessionStorage.removeItem('payCountdown')
    },
    gotoSuccess (returnUrl) {
      this.processing = false
      this.stopCountdown()
      this.clearCountdownCache()
      this.$router.push({
        name: 'PaySuccess',
        query: {
          mchOrderNo: (this.payOrderInfo && this.payOrderInfo.mchOrderNo) || '',
          amount: (this.payOrderInfo && this.payOrderInfo.amount) || '',
          mchName: (this.payOrderInfo && this.payOrderInfo.mchName) || '',
          returnUrl: returnUrl || ''
        }
      })
    },
    gotoFail (errMsg, returnUrl) {
      this.processing = false
      this.stopCountdown()
      this.clearCountdownCache()
      this.$router.push({
        name: 'PayFail',
        query: {
          mchOrderNo: (this.payOrderInfo && this.payOrderInfo.mchOrderNo) || '',
          amount: (this.payOrderInfo && this.payOrderInfo.amount) || '',
          errMsg: errMsg || '支付失败，请稍后再试',
          returnUrl: returnUrl || '',
          token: config.cacheToken || ''
        }
      })
    },
    formatAmount (amount) {
      if (!amount) return '0.00'
      return (amount / 100).toFixed(2)
    },
    handleImageError () {
      this.imageError = true
      this.showToastMessage('⚠️', '二维码加载失败，请刷新重试')
    },
    showToastMessage (icon, message) {
      this.toastIcon = icon
      this.toastMessage = message
      this.showToast = true
      setTimeout(() => {
        this.showToast = false
      }, 2000)
    }
  }
}
</script>

<style scoped>
.qrcashier-page {
  min-height: 100vh;
  background: linear-gradient(180deg, #f0f9ff 0%, #fafbff 100%);
  padding-bottom: 40px;
}

/* 页面头部 */
.page-header {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  padding: 20px;
  color: #fff;
  position: relative;
  overflow: hidden;
}

.page-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at 30% 50%, rgba(255, 255, 255, 0.1) 0%, transparent 50%);
  pointer-events: none;
}

.header-top {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  position: relative;
  z-index: 1;
}

.header-icon {
  font-size: 36px;
  flex-shrink: 0;
}

.header-info {
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.page-title {
  font-size: 20px;
  font-weight: 700;
  margin: 0;
  letter-spacing: 0.5px;
}

.header-countdown {
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 13px;
  font-weight: 600;
}

.header-countdown .countdown-icon {
  font-size: 14px;
}

.header-countdown .countdown-text {
  font-family: 'Courier New', monospace;
  letter-spacing: 1px;
}

.amount-info {
  text-align: center;
  padding: 16px 0 4px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  position: relative;
  z-index: 1;
}

.amount-value {
  font-size: 40px;
  font-weight: 700;
  margin-bottom: 8px;
  letter-spacing: -0.5px;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.amount-order {
  font-size: 12px;
  opacity: 0.9;
}

/* 内容容器 */
.content-wrapper {
  max-width: 600px;
  margin: 0 auto;
  padding: 20px 16px 0;
}

/* 二维码区域 */
.qrcode-section,
.steps-section {
  background: #fff;
  border-radius: 16px;
  padding: 24px 20px;
  margin-bottom: 16px;
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.06);
}

.section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 2px solid #f0f5ff;
}

.section-icon {
  font-size: 24px;
}

.section-title {
  font-size: 17px;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
}

.qrcode-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px 0;
}

.qrcode-wrapper {
  width: 280px;
  height: 280px;
  background: #fff;
  border-radius: 16px;
  padding: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.qrcode-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  border-radius: 8px;
}

.qrcode-mask {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #fff;
}

.error-icon {
  font-size: 48px;
}

.error-text {
  font-size: 16px;
  font-weight: 500;
}

.qrcode-placeholder {
  width: 280px;
  height: 280px;
  background: #f3f4f6;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #9ca3af;
}

.placeholder-icon {
  font-size: 64px;
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.placeholder-text {
  font-size: 16px;
  font-weight: 500;
}

.qrcode-tip {
  margin-top: 24px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border-radius: 12px;
  color: #1e40af;
  font-size: 14px;
  font-weight: 500;
}

.tip-icon {
  font-size: 18px;
}

/* 步骤引导 */
.steps-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.step-item {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}

.step-number {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 700;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.step-content {
  flex: 1;
  padding-top: 2px;
}

.step-title {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 4px;
}

.step-desc {
  font-size: 13px;
  color: #64748b;
  line-height: 1.6;
}

.highlight-amount {
  color: #10b981;
  font-weight: 700;
}

/* 加载遮罩 */
.processing-mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.processing-card {
  width: 260px;
  background: #fff;
  border-radius: 20px;
  padding: 32px 24px;
  text-align: center;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
}

.spinner {
  width: 56px;
  height: 56px;
  margin: 0 auto 20px;
  border: 4px solid #f0f4ff;
  border-top-color: #10b981;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.processing-text {
  font-size: 14px;
  color: #666;
  margin: 0;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Toast 提示 */
.toast {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2000;
  pointer-events: none;
}

.toast-content {
  background: rgba(0, 0, 0, 0.8);
  color: #fff;
  padding: 12px 24px;
  border-radius: 24px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.toast-icon {
  font-size: 18px;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

/* 响应式设计 */
@media (max-width: 480px) {
  .page-header {
    padding: 16px;
  }
  
  .header-icon {
    font-size: 32px;
  }
  
  .page-title {
    font-size: 18px;
  }
  
  .header-countdown {
    font-size: 12px;
    padding: 3px 10px;
  }
  
  .amount-value {
    font-size: 36px;
  }
  
  .qrcode-wrapper,
  .qrcode-placeholder {
    width: 240px;
    height: 240px;
  }
  
  .step-number {
    width: 28px;
    height: 28px;
    font-size: 14px;
  }
}
</style>
